import {
  DownloadTwoTone,
  FilterAltTwoTone,
  PhoneTwoTone,
  VisibilityTwoTone,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  guideGeneralCustomerListExcelExport,
  guideTourGeneralCustomerList,
} from '../api';
import { useAuth } from '../hooks/useAuth';
import { IGeneralCustomerList } from '../utils/types';
import { AgeGroupIcon } from './AgeGroupIcon';
import GeneralCustomerListFilterDialog from './GeneralCustomerListFilterDialog';

type GuideTourGeneralCustomerListProps = {
  salesIndividualId: string;
};

export default function GuideTourCustomerList({
  salesIndividualId,
}: GuideTourGeneralCustomerListProps) {
  const [generalCustomerList, setGeneralCustomerList] = useState<
    IGeneralCustomerList[]
  >([]);
  const { token } = useAuth();
  const { t } = useTranslation();
  const [, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  const [selectedTourGroups, setSelectedTourGroups] = useState<string[]>([]);
  const [visiblePhoneIndex, setVisiblePhoneIndex] = useState<number | null>(
    null,
  );

  const downloadExcel = () => {
    fetchGuideTourGeneralCustomerListExcelExport(salesIndividualId);
  };

  const fetchGuideTourGeneralCustomerListExcelExport = async (
    salesIndividualId: string,
  ) => {
    try {
      const res = await guideGeneralCustomerListExcelExport(
        salesIndividualId,
        token,
      );
      const blob = await res.blob();
      saveAs(blob, 'GeneralCustomerList.xlsx');
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    }
  };

  const tourGroupOptions = Array.from(
    new Set(
      generalCustomerList
        .filter(
          (row) => row?.tourGroup !== undefined && row?.tourGroup !== null,
        )
        .map((row) => row.tourGroup),
    ),
  );

  const salesGroupOptions = Array.from(
    new Set(
      generalCustomerList
        .filter(
          (row) =>
            row?.salesOrderGroup !== undefined && row?.salesOrderGroup !== null,
        )
        .map((row) => row.salesOrderGroup),
    ),
  );

  useEffect(() => {
    fetchGuideTourGeneralCustomerList(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchGuideTourGeneralCustomerList = async (id: string) => {
    try {
      const res = await guideTourGeneralCustomerList(id, token);
      const data = await res.json();
      if (res?.status === 200) {
        setGeneralCustomerList(data);
      } else {
        throw data;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneIconClick = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleShowNumber = (index: number) => {
    setVisiblePhoneIndex(visiblePhoneIndex === index ? null : index);
  };

  return (
    <Box>
      {loading && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress color="secondary" />
          <Typography sx={{ fontSize: 18, mt: 2 }}>
            {t('balancesQueryPage.loading')}
          </Typography>
        </Box>
      )}
      {!loading && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={downloadExcel} sx={{ mr: 1 }}>
                <DownloadTwoTone />
              </IconButton>
              <IconButton
                onClick={() => setOpenFilter(!openFilter)}
                sx={{ mr: 1 }}
              >
                <FilterAltTwoTone />
              </IconButton>
              <TextField
                label={t('guideTourAccommodationInfo.searchPerson')}
                placeholder={t('guideTourAccommodationInfo.searchWithName')}
                variant="outlined"
                color="secondary"
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{
                  width: '100%',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon color="secondary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
          <GeneralCustomerListFilterDialog
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            tourGroupOptions={tourGroupOptions}
            salesGroupOptions={salesGroupOptions}
            selectedTourGroups={selectedTourGroups}
            setSelectedTourGroups={setSelectedTourGroups}
            selectedSalesGroups={selectedSalesGroups}
            setSelectedSalesGroups={setSelectedSalesGroups}
          />
          <TableContainer component={Paper} sx={{ mt: 1, border: 'none' }}>
            <Table size="small" aria-label="customer table">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.phoneNumber')}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.customerName')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.disability')}
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.bedType')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.age')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.salesOffice')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.city')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.district')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.tourGroup')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.PNR')}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                  >
                    {t('guideGeneralCustomerList.orderNumber')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generalCustomerList
                  .filter(
                    (row) =>
                      row.fullName
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) &&
                      (selectedTourGroups.length === 0 ||
                        selectedTourGroups.includes(row.tourGroup)) &&
                      (selectedSalesGroups.length === 0 ||
                        selectedSalesGroups.includes(row.salesOrderGroup)),
                  )
                  .map((row, index: number) => (
                    <TableRow key={row.fullName}>
                      <TableCell
                        sx={{
                          border: (t) => '1px solid ' + t.palette.divider,
                        }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {visiblePhoneIndex === index ? null : (
                            <IconButton
                              size="small"
                              onClick={() =>
                                handlePhoneIconClick(`+${row.phoneNumber}`)
                              }
                            >
                              <PhoneTwoTone />
                            </IconButton>
                          )}
                          {visiblePhoneIndex === index ? (
                            <a href={`tel:+${row.phoneNumber}`}>
                              <Typography> {row.phoneNumber} </Typography>
                            </a>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleShowNumber(index)}
                            >
                              <VisibilityTwoTone />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="left"
                      >
                        <Stack alignItems="center" direction="row" gap={1}>
                          <AgeGroupIcon
                            startMonths={row.startAgeInMonth}
                            gender={row.gender as 'MALE' | 'FEMALE'}
                            sx={{ fontSize: 18 }}
                          />
                          {row.fullName}
                        </Stack>
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.disability || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.bedTypeCode || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.age || '0'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.salesOrderGroup || '-'}
                        {row.salesOffice != row.salesOrderGroup
                          ? '(' + row.salesOffice + ')'
                          : ''}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.city || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.district || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.tourGroup || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.pnr || '-'}
                      </TableCell>
                      <TableCell
                        sx={{ border: (t) => '1px solid ' + t.palette.divider }}
                        component="th"
                        scope="row"
                        align="center"
                      >
                        {row.salesOrderNumber || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
